// CallLogsTab.jsx - Modern Corporate Call Logs with Backend Search & Recording Playback
// Version: 2.0.0 - Added inline audio player for call recordings

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import {
    Phone, PhoneForwarded, PhoneOff, PhoneMissed,
    ChevronLeft, ChevronRight, ChevronDown, RefreshCw, Search, X,
    Voicemail, RotateCcw, Calendar, Filter, AlertCircle,
    Play, Pause, Volume2, UserX, Bot, Download
} from 'lucide-react'
import { monitoringApi, avatarApi, dialerApi, voiceSetApi } from '../services/api'
import CallDetailModal from '../components/CallDetailModal'
import './CallLogsTab.css'

// Disposition config
const DISPOSITION_CONFIG = {
    'transfer-call': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
    'call-transfered': { label: 'Transferred', color: '#10b981', icon: PhoneForwarded },
    'TRANSFER': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
    'not-interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
    'not_interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
    'do-not-call': { label: 'DNC', color: '#ef4444', icon: PhoneMissed },
    'do_not_call': { label: 'DNC', color: '#ef4444', icon: PhoneMissed },
    'answering-machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
    'answering_machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
    'no-answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
    'no_answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
    'call-back': { label: 'Callback', color: '#3b82f6', icon: RotateCcw },
    'call_back': { label: 'Callback', color: '#3b82f6', icon: RotateCcw },
}

const PAGE_SIZES = [50, 100, 250, 500]

// ============================================
// FUZZY MATCHING HELPERS
// For smart dialer + SIP search
// ============================================

/**
 * Calculate similarity score between two strings (0-1)
 * Uses a combination of includes, startsWith, and character matching
 */
function fuzzyMatch(query, target) {
    if (!query || !target) return 0
    const q = query.toLowerCase().trim()
    const t = target.toLowerCase().trim()

    // Exact match
    if (t === q) return 1

    // Starts with (high score)
    if (t.startsWith(q)) return 0.9

    // Contains (medium score)
    if (t.includes(q)) return 0.7

    // Character-by-character fuzzy (allows typos)
    // Check if all query chars appear in order in target
    let qIdx = 0
    let matched = 0
    for (let i = 0; i < t.length && qIdx < q.length; i++) {
        if (t[i] === q[qIdx]) {
            matched++
            qIdx++
        }
    }

    // If all query chars found in order
    if (qIdx === q.length) {
        // Score based on how compact the match is
        return 0.5 * (matched / t.length)
    }

    // Levenshtein-like: allow 1-2 character differences for short queries
    if (q.length >= 3) {
        const maxDiff = Math.floor(q.length / 3) // Allow 1 diff per 3 chars
        let diffs = 0
        for (let i = 0; i < Math.min(q.length, t.length); i++) {
            if (q[i] !== t[i]) diffs++
        }
        diffs += Math.abs(q.length - t.length)
        if (diffs <= maxDiff) return 0.4
    }

    return 0
}

/**
 * Parse search query to extract dialer name and SIP IDs
 * Example: "easyleads 8001 8002" -> { dialerQuery: "easyleads", sipIds: ["8001", "8002"] }
 */
function parseSmartSearch(query) {
    if (!query) return { dialerQuery: '', sipIds: [] }

    const parts = query.trim().split(/\s+/)
    const sipIds = []
    const textParts = []

    parts.forEach(part => {
        // SIP IDs are typically 4-digit numbers like 8001, 8002
        if (/^\d{3,5}$/.test(part)) {
            sipIds.push(part)
        } else {
            textParts.push(part)
        }
    })

    return {
        dialerQuery: textParts.join(' '),
        sipIds
    }
}

const formatDuration = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '0:00'
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '-'

// ============================================
// INLINE AUDIO PLAYER COMPONENT
// Compact player for table cells
// ============================================
function RecordingPlayer({ src, duration, onPlayStateChange }) {
    const [isPlaying, setIsPlaying] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const audioRef = useRef(null)

    const togglePlay = async (e) => {
        e.stopPropagation() // Don't trigger row click
        if (!audioRef.current) return

        try {
            if (isPlaying) {
                audioRef.current.pause()
                setIsPlaying(false)
                onPlayStateChange?.(null)
            } else {
                // Stop any other playing audio
                document.querySelectorAll('audio').forEach(a => {
                    if (a !== audioRef.current) a.pause()
                })
                setIsLoading(true)
                await audioRef.current.play()
                setIsPlaying(true)
                onPlayStateChange?.(src)
            }
        } catch (err) {
            console.error('Playback error:', err)
            setIsLoading(false)
        }
    }

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleEnded = () => {
            setIsPlaying(false)
            onPlayStateChange?.(null)
        }
        const handlePause = () => {
            setIsPlaying(false)
            setIsLoading(false)
        }
        const handlePlaying = () => setIsLoading(false)
        const handleError = () => {
            setIsLoading(false)
            setIsPlaying(false)
        }

        audio.addEventListener('ended', handleEnded)
        audio.addEventListener('pause', handlePause)
        audio.addEventListener('playing', handlePlaying)
        audio.addEventListener('error', handleError)

        return () => {
            audio.removeEventListener('ended', handleEnded)
            audio.removeEventListener('pause', handlePause)
            audio.removeEventListener('playing', handlePlaying)
            audio.removeEventListener('error', handleError)
        }
    }, [onPlayStateChange])

    if (!src) {
        return <span className="cl-no-recording">—</span>
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <button
                className={`cl-play-btn ${isPlaying ? 'playing' : ''} ${isLoading ? 'loading' : ''}`}
                onClick={togglePlay}
                title={duration ? `${duration}s recording` : 'Play recording'}
            >
                <audio ref={audioRef} src={src} preload="none" />
                {isLoading ? (
                    <span className="cl-play-spinner" />
                ) : isPlaying ? (
                    <Pause size={14} />
                ) : (
                    <Play size={14} />
                )}
                {duration > 0 && <span className="cl-play-duration">{duration}s</span>}
            </button>
            <a href={src} download className="cl-download-btn" title="Download recording" onClick={e => e.stopPropagation()}>
                <Download size={14} />
            </a>
        </div>
    )
}

// ============================================
// MAIN COMPONENT
// ============================================
// Searchable Dropdown Component
function SearchableSelect({ options, value, onChange, placeholder, labelKey = 'name', valueKey = 'id', allLabel = 'All' }) {
    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')
    const dropdownRef = useRef(null)

    const filteredOptions = options.filter(opt =>
        (opt[labelKey] || '').toLowerCase().includes(search.toLowerCase())
    )

    const selectedOption = options.find(opt => String(opt[valueKey]) === String(value))

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
                setSearch('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className="cl-searchable-select" ref={dropdownRef}>
            <button
                type="button"
                className={`cl-searchable-select__trigger ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{selectedOption ? selectedOption[labelKey] : allLabel}</span>
                <ChevronDown size={14} />
            </button>
            {isOpen && (
                <div className="cl-searchable-select__dropdown">
                    <div className="cl-searchable-select__search">
                        <Search size={14} />
                        <input
                            type="text"
                            placeholder={`Search ${placeholder}...`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                        {search && (
                            <button onClick={() => setSearch('')}><X size={12} /></button>
                        )}
                    </div>
                    <div className="cl-searchable-select__options">
                        <div
                            className={`cl-searchable-select__option ${!value ? 'selected' : ''}`}
                            onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
                        >
                            {allLabel}
                        </div>
                        {filteredOptions.map(opt => (
                            <div
                                key={opt[valueKey]}
                                className={`cl-searchable-select__option ${String(opt[valueKey]) === String(value) ? 'selected' : ''}`}
                                onClick={() => { onChange(String(opt[valueKey])); setIsOpen(false); setSearch(''); }}
                            >
                                {opt[labelKey]}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="cl-searchable-select__empty">No matches</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default function CallLogsTab({ dateFrom, dateTo, initialSearch = '', initialLicenseId = '', initialDialerId = '' }) {
    // Data sources
    const [avatars, setAvatars] = useState([])
    const [dialers, setDialers] = useState([])
    const [voiceSets, setVoiceSets] = useState([])
    const [allBots, setAllBots] = useState([])

    // Call logs data - server-side pagination
    const [rawCallLogs, setRawCallLogs] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)

    // Search state - initialize with URL param if provided
    const [searchQuery, setSearchQuery] = useState(initialSearch)
    const [searchResults, setSearchResults] = useState(null) // null = not searching, [] = no results
    const [searchMeta, setSearchMeta] = useState(null)
    const [isSearching, setIsSearching] = useState(false)
    const searchTimeout = useRef(null)

    // Smart search state (dialer + SIP autocomplete)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [smartSearchActive, setSmartSearchActive] = useState(false)
    const [smartSearchDialer, setSmartSearchDialer] = useState(null) // { id, name }
    const [smartSearchSips, setSmartSearchSips] = useState([]) // ["8001", "8002"]
    const searchInputRef = useRef(null)

    // Filters - initialize selectedDialer with prop if provided
    const [selectedAvatar, setSelectedAvatar] = useState('')
    const [selectedDialer, setSelectedDialer] = useState(initialDialerId ? String(initialDialerId) : '')
    const [selectedSip, setSelectedSip] = useState('')
    const [selectedVoiceSet, setSelectedVoiceSet] = useState('')
    const [selectedDisposition, setSelectedDisposition] = useState('')

    // UI State
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(100)
    const [selectedCall, setSelectedCall] = useState(null)
    const [showFilters, setShowFilters] = useState(false)
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null) // Track which audio is playing

    // Load filter options
    useEffect(() => {
        Promise.allSettled([
            avatarApi.getAll(),
            dialerApi.getAll(),
            monitoringApi.getAllStatus(),
            voiceSetApi.getAll()
        ]).then(([avatarsRes, dialersRes, botsRes, voiceSetsRes]) => {
            if (avatarsRes.status === 'fulfilled') setAvatars(avatarsRes.value.data || [])
            if (dialersRes.status === 'fulfilled') setDialers(dialersRes.value.data || [])
            if (botsRes.status === 'fulfilled') setAllBots(botsRes.value.data || [])
            if (voiceSetsRes.status === 'fulfilled') {
                const vsData = voiceSetsRes.value.data
                setVoiceSets(Array.isArray(vsData) ? vsData : [])
            }
        })
    }, [])

    // Fetch call logs with server-side pagination
    const fetchCallLogs = useCallback(async (page = 1) => {
        setLoading(true)
        try {
            const offset = (page - 1) * pageSize
            const params = {
                limit: pageSize,
                offset: offset,
                from: dateFrom,
                to: dateTo
            }

            // Add filters if selected
            if (selectedDialer) params.dialer_id = selectedDialer
            if (selectedSip) params.sip_id = selectedSip
            if (selectedVoiceSet) params.voice_set_id = selectedVoiceSet
            if (selectedDisposition) params.disposition = selectedDisposition
            if (initialLicenseId) params.license_id = initialLicenseId

            const res = await monitoringApi.getDispositions(params)
            const data = res.data?.data || res.data || []
            const total = res.data?.total || data.length

            setRawCallLogs(data)
            setTotalRecords(total)
        } catch (err) {
            console.error('Fetch error:', err)
            setRawCallLogs([])
            setTotalRecords(0)
        } finally {
            setLoading(false)
        }
    }, [dateFrom, dateTo, pageSize, selectedDialer, selectedSip, selectedVoiceSet, selectedDisposition, initialLicenseId])

    // Fetch when page changes or filters change (not in search mode)
    useEffect(() => {
        if (searchResults === null && !smartSearchActive) {
            fetchCallLogs(currentPage)
        }
    }, [currentPage, pageSize, dateFrom, dateTo, selectedDialer, selectedSip, selectedVoiceSet, selectedDisposition, fetchCallLogs])

    // Debounced search - calls backend API
    useEffect(() => {
        // Clear previous timeout
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current)
        }

        // If search is cleared, reset to normal mode
        if (!searchQuery || searchQuery.trim().length < 2) {
            setSearchResults(null)
            setSearchMeta(null)
            setIsSearching(false)
            return
        }

        // Debounce search by 500ms
        setIsSearching(true)
        searchTimeout.current = setTimeout(async () => {
            try {
                console.log('[Search] Searching for:', searchQuery)

                const res = await monitoringApi.search({
                    q: searchQuery.trim(),
                    start_date: dateFrom,
                    end_date: dateTo,
                    limit: 500
                })

                const data = res.data?.data || []
                const meta = res.data?.meta || {}

                console.log('[Search] Found:', data.length, 'results', meta)

                setSearchResults(data)
                setSearchMeta(meta)
                setCurrentPage(1)
            } catch (err) {
                console.error('[Search] Error:', err)
                setSearchResults([])
                setSearchMeta({ error: true })
            } finally {
                setIsSearching(false)
            }
        }, 500)

        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current)
        }
    }, [searchQuery, dateFrom, dateTo])

    // Get avatar SIPs for filtering
    const avatarSipMap = useMemo(() => {
        const map = {}
        allBots.forEach(bot => {
            if (bot.avatar_id) {
                if (!map[bot.avatar_id]) map[bot.avatar_id] = []
                map[bot.avatar_id].push(bot.sip_id)
            }
        })
        return map
    }, [allBots])

    // Skip client-side date filtering - server already filters by date
    // This avoids timezone mismatches between server UTC and client local time
    const dateFilteredLogs = useMemo(() => {
        return rawCallLogs
    }, [rawCallLogs])

    // Apply filters (to either search results or date filtered logs)
    // NOTE: Server already filters by dialer_id, sip_id, voice_set_id, disposition
    // We only need client-side filtering for: avatar, smart search, license_id, and search results
    const filteredLogs = useMemo(() => {
        // If searching, use search results as base
        let data = searchResults !== null ? [...searchResults] : [...dateFilteredLogs]

        // Smart search filter (dialer + SIPs from autocomplete) - client-side
        if (smartSearchActive && smartSearchDialer) {
            data = data.filter(c => String(c.dialer_id) === String(smartSearchDialer.id))

            // If specific SIPs selected, filter by those too
            if (smartSearchSips.length > 0) {
                data = data.filter(c => smartSearchSips.includes(c.sip_id))
            }
        }

        // Avatar filter - client-side only (not supported by server)
        if (selectedAvatar) {
            const sips = avatarSipMap[selectedAvatar] || []
            data = data.filter(c =>
                String(c.avatar_id) === selectedAvatar || sips.includes(c.sip_id)
            )
        }

        // Server-side filters: dialer_id, sip_id, voice_set_id, disposition, license_id
        // Only apply client-side if we're in search mode (server didn't filter these)
        if (searchResults !== null) {
            if (selectedDialer && !smartSearchActive) {
                data = data.filter(c => String(c.dialer_id) === selectedDialer)
            }
            if (selectedSip && (!smartSearchActive || smartSearchSips.length === 0)) {
                data = data.filter(c => c.sip_id === selectedSip)
            }
            if (selectedVoiceSet) {
                data = data.filter(c => String(c.voice_set_id) === selectedVoiceSet)
            }
            if (selectedDisposition) {
                data = data.filter(c =>
                    (c.disposition || '').toLowerCase().includes(selectedDisposition.toLowerCase())
                )
            }
        }

        return data
    }, [searchResults, dateFilteredLogs, selectedAvatar, selectedDialer, selectedSip, selectedVoiceSet, selectedDisposition, avatarSipMap, smartSearchActive, smartSearchDialer, smartSearchSips])

    // Pagination - server-side for most cases, client-side only for search/smart-search mode
    // Server supports: dialer_id, sip_id, voice_set_id, disposition, license_id filters
    // Client-side only: avatar filter, smart search, search results
    const needsClientPagination = selectedAvatar || smartSearchActive || searchResults !== null
    const isServerPaginated = !needsClientPagination

    const totalPages = isServerPaginated
        ? Math.ceil(totalRecords / pageSize)
        : Math.ceil(filteredLogs.length / pageSize)

    const paginatedLogs = isServerPaginated
        ? filteredLogs  // Already paginated from server
        : filteredLogs.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    // Reset page on filter change
    useEffect(() => { setCurrentPage(1) }, [selectedAvatar, selectedDialer, selectedSip, selectedVoiceSet, selectedDisposition])

    // Available SIPs based on current filters
    const availableSips = useMemo(() => {
        let sips = allBots
        if (selectedAvatar) sips = sips.filter(b => String(b.avatar_id) === selectedAvatar)
        if (selectedDialer) sips = sips.filter(b => String(b.dialer_id) === selectedDialer)
        return sips
    }, [allBots, selectedAvatar, selectedDialer])

    // Disposition breakdown
    const dispositionStats = useMemo(() => {
        const stats = {}
        filteredLogs.forEach(c => {
            const d = c.disposition || 'unknown'
            stats[d] = (stats[d] || 0) + 1
        })
        return Object.entries(stats)
            .map(([disposition, count]) => ({ disposition, count }))
            .sort((a, b) => b.count - a.count)
    }, [filteredLogs])

    // Recording stats
    const recordingCount = useMemo(() => {
        return filteredLogs.filter(c => c.recording_url).length
    }, [filteredLogs])

    // Helpers
    const getDispoConfig = (d) => DISPOSITION_CONFIG[d] || { label: d || 'Unknown', color: '#6b7280', icon: Phone }
    const getDialerName = (id) => dialers.find(d => String(d.id) === String(id))?.name || '-'
    const getVoiceSetName = (id) => {
        if (!id) return '-'
        const vs = voiceSets.find(v => String(v.id) === String(id))
        return vs?.name || `VS-${id}`
    }
    const getAvatarName = (call) => {
        if (call.avatar_id) {
            const av = avatars.find(a => String(a.id) === String(call.avatar_id))
            if (av) return av.name
        }
        const bot = allBots.find(b => b.sip_id === call.sip_id)
        if (bot?.avatar_id) {
            const av = avatars.find(a => String(a.id) === String(bot.avatar_id))
            return av?.name || '-'
        }
        return '-'
    }

    const clearFilters = () => {
        setSelectedAvatar('')
        setSelectedDialer('')
        setSelectedSip('')
        setSelectedVoiceSet('')
        setSelectedDisposition('')
        setCurrentPage(1)
    }

    const clearSearch = () => {
        setSearchQuery('')
        setSearchResults(null)
        setSearchMeta(null)
        setCurrentPage(1)
    }

    const hasFilters = selectedAvatar || selectedDialer || selectedSip || selectedVoiceSet || selectedDisposition
    const isInSearchMode = searchResults !== null

    // Get bot info for license_id filter badge
    const licenseBotInfo = useMemo(() => {
        if (!initialLicenseId) return null
        const bot = allBots.find(b => String(b.license_id) === initialLicenseId)
        if (bot) {
            const dialer = dialers.find(d => String(d.id) === String(bot.dialer_id))
            return { sip_id: bot.sip_id, dialer_name: dialer?.name || '' }
        }
        return null
    }, [initialLicenseId, allBots, dialers])

    // ============================================
    // SMART SEARCH: Dialer + SIP autocomplete
    // ============================================

    // Build dialer suggestions with their SIPs
    const dialerSuggestions = useMemo(() => {
        if (!searchQuery || searchQuery.trim().length < 2) return []

        const { dialerQuery, sipIds } = parseSmartSearch(searchQuery)
        if (!dialerQuery) return []

        // Find matching dialers
        const matches = dialers
            .map(dialer => ({
                ...dialer,
                score: fuzzyMatch(dialerQuery, dialer.name),
                sips: allBots
                    .filter(b => String(b.dialer_id) === String(dialer.id))
                    .map(b => b.sip_id)
                    .filter((v, i, a) => a.indexOf(v) === i) // unique
                    .sort()
            }))
            .filter(d => d.score > 0.3 && d.sips.length > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 5)

        return matches
    }, [searchQuery, dialers, allBots])

    // Handle selecting a suggestion
    const selectSuggestion = (dialer, sips = []) => {
        const sipText = sips.length > 0 ? ' ' + sips.join(' ') : ''
        setSearchQuery(dialer.name + sipText)
        setSmartSearchDialer({ id: dialer.id, name: dialer.name })
        setSmartSearchSips(sips)
        setSmartSearchActive(true)
        setShowSuggestions(false)
        // Clear backend search results - we'll filter locally
        setSearchResults(null)
        setSearchMeta(null)
    }

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchQuery(value)

        // If user clears or significantly changes the query, reset smart search
        if (!value || value.trim().length < 2) {
            setSmartSearchActive(false)
            setSmartSearchDialer(null)
            setSmartSearchSips([])
            setShowSuggestions(false)
            return
        }

        const { dialerQuery, sipIds } = parseSmartSearch(value)

        // If smart search is already active, update the SIP IDs dynamically
        if (smartSearchActive && smartSearchDialer) {
            // Check if the dialer name is still in the query
            const dialerNameLower = smartSearchDialer.name.toLowerCase()
            const queryLower = value.toLowerCase()

            if (queryLower.includes(dialerNameLower) || fuzzyMatch(dialerQuery, smartSearchDialer.name) > 0.5) {
                // Update SIP IDs
                setSmartSearchSips(sipIds)
                setShowSuggestions(false)
                return
            } else {
                // User changed the dialer name, reset smart search
                setSmartSearchActive(false)
                setSmartSearchDialer(null)
                setSmartSearchSips([])
            }
        }

        // Show suggestions if typing text (not just numbers)
        setShowSuggestions(dialerQuery.length >= 2)
    }

    // Handle clicking outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
                setShowSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Clear smart search
    const clearSmartSearch = () => {
        setSearchQuery('')
        setSmartSearchActive(false)
        setSmartSearchDialer(null)
        setSmartSearchSips([])
        setSearchResults(null)
        setSearchMeta(null)
        setShowSuggestions(false)
    }

    return (
        <div className="call-logs-modern">
            {/* Header Bar */}
            <div className="cl-header">
                <div className="cl-header-left">
                    <h2>
                        <Phone size={20} />
                        Call Logs
                    </h2>
                    <span className="cl-count">
                        {(isServerPaginated ? totalRecords : filteredLogs.length).toLocaleString()} calls
                        {recordingCount > 0 && (
                            <span className="cl-recording-count" title="Calls with recordings">
                                <Volume2 size={12} /> {recordingCount}
                            </span>
                        )}
                    </span>
                </div>

                <div className="cl-header-right">
                    {/* Search Box with Autocomplete */}
                    <div className={`cl-search-wrapper ${showSuggestions && dialerSuggestions.length > 0 ? 'has-suggestions' : ''}`} ref={searchInputRef}>
                        <div className={`cl-search-box ${isInSearchMode ? 'active' : ''} ${smartSearchActive ? 'smart-active' : ''}`}>
                            <Search size={16} />
                            <input
                                type="text"
                                placeholder="Search phone, dialer, SIP, or transcriptions..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => {
                                    const { dialerQuery } = parseSmartSearch(searchQuery)
                                    if (dialerQuery.length >= 2) setShowSuggestions(true)
                                }}
                            />
                            {searchQuery && (
                                <button className="cl-search-clear" onClick={clearSmartSearch}>
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        {/* Autocomplete Dropdown */}
                        {showSuggestions && dialerSuggestions.length > 0 && (
                            <div className="cl-autocomplete">
                                <div className="cl-autocomplete-header">
                                    <span>Dialers</span>
                                    <span className="hint">Click dialer, then add SIP IDs</span>
                                </div>
                                {dialerSuggestions.map(dialer => (
                                    <div key={dialer.id} className="cl-autocomplete-item">
                                        <button
                                            className="cl-autocomplete-dialer"
                                            onClick={() => selectSuggestion(dialer)}
                                        >
                                            <span className="dialer-name">{dialer.name}</span>
                                            <span className="sip-count">{dialer.sips.length} SIPs</span>
                                        </button>
                                        <div className="cl-autocomplete-sips">
                                            {dialer.sips.slice(0, 8).map(sip => (
                                                <button
                                                    key={sip}
                                                    className="sip-chip"
                                                    onClick={() => selectSuggestion(dialer, [sip])}
                                                    title={`Filter ${dialer.name} → ${sip}`}
                                                >
                                                    {sip}
                                                </button>
                                            ))}
                                            {dialer.sips.length > 8 && (
                                                <span className="sip-more">+{dialer.sips.length - 8}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Smart Search Active Badge */}
                    {smartSearchActive && smartSearchDialer && (
                        <div className="cl-smart-search-badge">
                            <span className="dialer-name">{smartSearchDialer.name}</span>
                            {smartSearchSips.length > 0 && (
                                <span className="sip-list">{smartSearchSips.join(', ')}</span>
                            )}
                            <button onClick={clearSmartSearch} title="Clear filter">
                                <X size={12} />
                            </button>
                        </div>
                    )}

                    {/* Search Info Badge */}
                    {isInSearchMode && searchMeta && !smartSearchActive && (
                        <div className={`cl-search-info ${searchMeta.date_range?.limited || searchMeta.warning ? 'limited' : ''}`}>
                            {searchMeta.total_matches ? (
                                <>
                                    <span>{searchMeta.total_matches} matches</span>
                                    {searchMeta.date_range?.limited && (
                                        <span className="cl-search-warning" title="Search limited to 7 days for performance">
                                            <AlertCircle size={12} /> 7-day limit
                                        </span>
                                    )}
                                </>
                            ) : searchMeta.warning ? (
                                <span className="cl-search-warning" title={searchMeta.warning}>
                                    <AlertCircle size={12} /> {searchMeta.warning}
                                </span>
                            ) : searchMeta.error ? (
                                <span className="error">Search error</span>
                            ) : (
                                <span>No matches</span>
                            )}
                        </div>
                    )}

                    {/* Bot Filter Badge (from BotsTab navigation) */}
                    {licenseBotInfo && (
                        <div className="cl-bot-filter-badge" title="Filtering by specific bot">
                            <Bot size={14} />
                            <span>{licenseBotInfo.sip_id}</span>
                            {licenseBotInfo.dialer_name && <span className="dialer-tag">{licenseBotInfo.dialer_name}</span>}
                        </div>
                    )}

                    <button className={`cl-filter-toggle ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
                        <Filter size={16} />
                        Filters
                        {hasFilters && <span className="cl-filter-dot" />}
                    </button>

                    <button className="cl-refresh-btn" onClick={() => fetchCallLogs(currentPage)} disabled={loading}>
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
                <motion.div
                    className="cl-filters"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                >
                    <div className="cl-filter-grid">
                        <div className="cl-filter-item">
                            <label>Avatar</label>
                            <SearchableSelect
                                options={avatars.filter(a => !a.archived)}
                                value={selectedAvatar}
                                onChange={(val) => { setSelectedAvatar(val); setSelectedSip(''); }}
                                placeholder="avatars"
                                allLabel="All Avatars"
                            />
                        </div>
                        <div className="cl-filter-item">
                            <label>Dialer</label>
                            <SearchableSelect
                                options={dialers.filter(d => !d.archived)}
                                value={selectedDialer}
                                onChange={(val) => { setSelectedDialer(val); setSelectedSip(''); }}
                                placeholder="dialers"
                                allLabel="All Dialers"
                            />
                        </div>
                        <div className="cl-filter-item">
                            <label>SIP</label>
                            <SearchableSelect
                                options={availableSips.map(s => ({ id: s.sip_id, name: s.sip_id }))}
                                value={selectedSip}
                                onChange={setSelectedSip}
                                placeholder="SIPs"
                                allLabel={`All SIPs (${availableSips.length})`}
                            />
                        </div>
                        <div className="cl-filter-item">
                            <label>Voice Set</label>
                            <SearchableSelect
                                options={voiceSets.filter(v => !v.archived)}
                                value={selectedVoiceSet}
                                onChange={setSelectedVoiceSet}
                                placeholder="voice sets"
                                allLabel="All Voice Sets"
                            />
                        </div>
                        <div className="cl-filter-item">
                            <label>Disposition</label>
                            <SearchableSelect
                                options={[
                                    { id: 'transfer', name: 'Transfers' },
                                    { id: 'not-interested', name: 'Not Interested' },
                                    { id: 'do-not-call', name: 'Do Not Call' },
                                    { id: 'answering-machine', name: 'Voicemail' },
                                    { id: 'no-answer', name: 'No Answer' },
                                    { id: 'call-back', name: 'Callback' }
                                ]}
                                value={selectedDisposition}
                                onChange={setSelectedDisposition}
                                placeholder="dispositions"
                                allLabel="All Dispositions"
                            />
                        </div>
                    </div>
                    {hasFilters && (
                        <button className="cl-clear-btn" onClick={clearFilters}>
                            <X size={14} /> Clear all filters
                        </button>
                    )}
                </motion.div>
            )}

            {/* Disposition Summary Cards */}
            {dispositionStats.length > 0 && !isInSearchMode && (
                <div className="cl-dispo-row">
                    {dispositionStats.slice(0, 6).map((stat, i) => {
                        const config = getDispoConfig(stat.disposition)
                        const Icon = config.icon
                        const isActive = selectedDisposition === stat.disposition
                        return (
                            <button
                                key={i}
                                className={`cl-dispo-card ${isActive ? 'active' : ''}`}
                                style={{ '--accent': config.color }}
                                onClick={() => setSelectedDisposition(isActive ? '' : stat.disposition)}
                            >
                                <Icon size={18} />
                                <span className="cl-dispo-count">{stat.count}</span>
                                <span className="cl-dispo-label">{config.label}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Table */}
            <div className="cl-table-wrapper">
                {(loading || isSearching) ? (
                    <div className="cl-loading">
                        <div className="cl-spinner" />
                        <span>{isSearching ? 'Searching transcriptions...' : 'Loading calls...'}</span>
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="cl-empty">
                        <Phone size={40} />
                        <p>{isInSearchMode ? 'No matches found' : 'No calls found'}</p>
                        <span>{isInSearchMode ? 'Try a different search term' : 'Try adjusting filters or date range'}</span>
                    </div>
                ) : (
                    <table className="cl-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Phone</th>
                                <th>Disposition</th>
                                <th>Duration</th>
                                <th className="cl-th-recording">Recording</th>
                                <th>Hung Up</th>
                                <th>Step</th>
                                <th>Voice Set</th>
                                <th>SIP</th>
                                <th>Dialer</th>
                                <th>Avatar</th>
                                {isInSearchMode && <th>Match</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedLogs.map((call, i) => {
                                const config = getDispoConfig(call.disposition)
                                const Icon = config.icon
                                // Determine row class based on disposition
                                const dispo = (call.disposition || '').toLowerCase()
                                let rowClass = ''
                                if (dispo.includes('transfer')) rowClass = 'dispo-transfer'
                                else if (dispo.includes('answering') || dispo.includes('voicemail')) rowClass = 'dispo-voicemail'
                                else if (dispo.includes('not-interested') || dispo.includes('not_interested')) rowClass = 'dispo-not-interested'
                                else if (dispo.includes('do-not-call') || dispo.includes('do_not_call') || dispo.includes('dnc')) rowClass = 'dispo-dnc'
                                else if (dispo.includes('no-answer') || dispo.includes('no_answer')) rowClass = 'dispo-no-answer'
                                else if (dispo.includes('call-back') || dispo.includes('call_back') || dispo.includes('callback')) rowClass = 'dispo-callback'

                                return (
                                    <tr
                                        key={call.id || call.call_id || i}
                                        className={`${rowClass} ${currentlyPlaying === call.recording_url ? 'playing' : ''}`}
                                        onClick={() => setSelectedCall(call)}
                                    >
                                        <td className="cl-td-time">{formatDateTime(call.call_end)}</td>
                                        <td className="cl-td-phone">{call.phone_number || call.phone_masked || '-'}</td>
                                        <td>
                                            <span className="cl-dispo-badge" style={{ '--c': config.color }}>
                                                <Icon size={12} />
                                                {config.label}
                                            </span>
                                        </td>
                                        <td className="cl-td-duration">{formatDuration(call.call_duration)}</td>
                                        <td className="cl-td-recording">
                                            <RecordingPlayer
                                                src={call.recording_url}
                                                duration={call.recording_duration}
                                                onPlayStateChange={setCurrentlyPlaying}
                                            />
                                        </td>
                                        <td className="cl-td-hungup">
                                            {call.hung_up_by === 'caller' ? (
                                                <span className="cl-hungup-badge caller" title="Caller hung up">
                                                    <UserX size={12} /> Caller
                                                </span>
                                            ) : call.hung_up_by === 'agent' ? (
                                                <span className="cl-hungup-badge agent" title="Agent hung up">
                                                    <Bot size={12} /> Agent
                                                </span>
                                            ) : (
                                                <span className="cl-hungup-badge unknown">—</span>
                                            )}
                                        </td>
                                        <td className="cl-td-step">{call.script_step_reached || '-'}</td>
                                        <td
                                            className="cl-td-voiceset cl-td-clickable"
                                            onClick={(e) => { e.stopPropagation(); setSelectedVoiceSet(String(call.voice_set_id || '')); }}
                                            title={`Filter by ${getVoiceSetName(call.voice_set_id)}`}
                                        >
                                            {getVoiceSetName(call.voice_set_id)}
                                        </td>
                                        <td
                                            className="cl-td-sip cl-td-clickable"
                                            onClick={(e) => { e.stopPropagation(); setSelectedSip(call.sip_id || ''); }}
                                            title={`Filter by ${call.sip_id}`}
                                        >
                                            {call.sip_id}
                                        </td>
                                        <td
                                            className="cl-td-muted cl-td-clickable"
                                            onClick={(e) => { e.stopPropagation(); setSelectedDialer(String(call.dialer_id || '')); }}
                                            title={`Filter by ${getDialerName(call.dialer_id)}`}
                                        >
                                            {getDialerName(call.dialer_id)}
                                        </td>
                                        <td
                                            className="cl-td-muted cl-td-clickable"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Find avatar id from call or from bot mapping
                                                const avatarId = call.avatar_id || allBots.find(b => b.sip_id === call.sip_id)?.avatar_id;
                                                if (avatarId) setSelectedAvatar(String(avatarId));
                                            }}
                                            title={`Filter by ${getAvatarName(call)}`}
                                        >
                                            {getAvatarName(call)}
                                        </td>
                                        {isInSearchMode && (
                                            <td>
                                                <span className={`cl-match-badge ${call.match_source}`}>
                                                    {call.match_source === 'transcription' ? '📝' :
                                                        call.match_source === 'both' ? '✓✓' : '✓'}
                                                </span>
                                            </td>
                                        )}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 0 && !loading && !isSearching && (
                <div className="cl-pagination">
                    <div className="cl-page-info">
                        Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, isServerPaginated ? totalRecords : filteredLogs.length)} of {(isServerPaginated ? totalRecords : filteredLogs.length).toLocaleString()}
                    </div>
                    <div className="cl-page-controls">
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}>
                            {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                        </select>
                        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                        <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                            <ChevronLeft size={16} />
                        </button>
                        <span>{currentPage} / {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                            <ChevronRight size={16} />
                        </button>
                        <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                </div>
            )}

            {/* Call Detail Modal */}
            {selectedCall && (
                <CallDetailModal
                    callId={selectedCall.call_id}
                    disposition={selectedCall}
                    onClose={() => setSelectedCall(null)}
                />
            )}
        </div>
    )
}